import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSites } from "@/contexts/SiteContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Info, AlertTriangle, ExternalLink,
} from "lucide-react";

const fmtInt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("fr-CH"));
const fmtCtr = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(2).replace(".", ",")} %`);
const fmtPos = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(1).replace(".", ","));

const STATUS_META = {
  CONNECTED: { label: "CONNECTÉ", cls: "bg-emerald-100 text-emerald-700" },
  NOT_CONNECTED: { label: "GSC NON CONNECTÉ", cls: "bg-slate-200 text-slate-600" },
  PROPERTY_NOT_SET: { label: "PROPRIÉTÉ NON CONFIGURÉE", cls: "bg-amber-100 text-amber-800" },
  AUTH_EXPIRED: { label: "AUTORISATION EXPIRÉE", cls: "bg-red-100 text-red-700" },
  INSUFFICIENT_PERMISSION: { label: "PERMISSION INSUFFISANTE", cls: "bg-red-100 text-red-700" },
  QUOTA_EXCEEDED: { label: "QUOTA GSC ATTEINT", cls: "bg-amber-100 text-amber-800" },
  TIMEOUT: { label: "ERREUR GSC (DÉLAI)", cls: "bg-red-100 text-red-700" },
  ERROR: { label: "ERREUR GSC", cls: "bg-red-100 text-red-700" },
};

const SIGNAL_CLS = {
  HIGH: "bg-violet-100 text-violet-700", MEDIUM: "bg-blue-100 text-blue-700",
  LOW: "bg-slate-100 text-slate-600", NONE: "bg-slate-100 text-slate-400",
  NOT_AVAILABLE: "bg-slate-100 text-slate-400",
};
const SIGNAL_LABEL = { HIGH: "Élevé", MEDIUM: "Moyen", LOW: "Faible", NONE: "Aucun signal", NOT_AVAILABLE: "Non disponible" };
const PRIO_CLS = {
  P0: "bg-red-600 text-white", P1: "bg-orange-500 text-white",
  P2: "bg-amber-400 text-slate-900", P3: "bg-slate-300 text-slate-700",
};

function Delta({ m, format = fmtInt, lowerIsBetter = false, testid }) {
  if (!m || m.status === "NOT_AVAILABLE") return <span data-testid={testid} className="text-xs text-slate-400">NON DISPONIBLE</span>;
  if (m.status === "NO_PREVIOUS") return <span data-testid={testid} className="text-xs text-slate-400">pas de période précédente</span>;
  if (m.status === "NEW") return <span data-testid={testid} className="text-xs font-semibold text-emerald-600">NOUVEAU</span>;
  if (m.delta === null || m.delta === undefined) return <span data-testid={testid} className="text-xs text-slate-400">variation n.c.</span>;
  const good = lowerIsBetter ? m.delta < 0 : m.delta > 0;
  const zero = m.delta === 0;
  const cls = zero ? "text-slate-500" : good ? "text-emerald-600" : "text-red-600";
  const sign = m.delta > 0 ? "+" : "";
  return (
    <span data-testid={testid} className={`text-xs font-medium ${cls} inline-flex items-center gap-1`}>
      {!zero && (good ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
      {sign}{format(m.delta)}{m.delta_pct !== null && m.delta_pct !== undefined ? ` (${m.delta_pct > 0 ? "+" : ""}${String(m.delta_pct).replace(".", ",")} %)` : " (% n.c.)"}
    </span>
  );
}

function KpiCard({ title, m, format, lowerIsBetter, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs text-slate-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-slate-900">
        {!m || m.current === null || m.current === undefined ? "NON DISPONIBLE" : format(m.current)}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Delta m={m} format={format} lowerIsBetter={lowerIsBetter} testid={`${testid}-delta`} />
      </div>
      {m && m.previous !== null && m.previous !== undefined && (
        <p className="text-[11px] text-slate-400 mt-1">période précédente : {format(m.previous)}</p>
      )}
    </div>
  );
}

function MetricCells({ r }) {
  return (
    <>
      <td className="px-2 py-1.5 text-right">{fmtInt(r.clicks)}</td>
      <td className="px-2 py-1.5 text-right">{fmtInt(r.impressions)}</td>
      <td className="px-2 py-1.5 text-right">{fmtCtr(r.ctr)}</td>
      <td className="px-2 py-1.5 text-right">{fmtPos(r.position)}</td>
    </>
  );
}

function MovementTable({ rows, keyField, testid, onKeyClick, deltaCols = true }) {
  if (!rows || rows.length === 0) return <p className="text-xs text-slate-400 px-2 py-2">Aucune ligne dans les données GSC de la période.</p>;
  return (
    <div className="overflow-x-auto">
      <table data-testid={testid} className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200 text-left">
            <th className="px-2 py-1.5">{keyField === "page" ? "URL" : "Requête"}</th>
            <th className="px-2 py-1.5 text-right">Clics</th>
            <th className="px-2 py-1.5 text-right">Impressions</th>
            <th className="px-2 py-1.5 text-right">CTR</th>
            <th className="px-2 py-1.5 text-right">Position moy. GSC</th>
            {deltaCols && <th className="px-2 py-1.5 text-right">Δ clics</th>}
            {deltaCols && <th className="px-2 py-1.5 text-right">Δ impressions</th>}
            {deltaCols && <th className="px-2 py-1.5 text-right">Δ position</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-2 py-1.5 max-w-[320px] truncate">
                <button className="text-blue-700 hover:underline text-left truncate max-w-full"
                  data-testid={`${testid}-key-${idx}`} onClick={() => onKeyClick?.(r[keyField])}>
                  {r[keyField]}
                </button>
              </td>
              <MetricCells r={r} />
              {deltaCols && <td className={`px-2 py-1.5 text-right ${r.delta_clicks > 0 ? "text-emerald-600" : r.delta_clicks < 0 ? "text-red-600" : "text-slate-500"}`}>{r.delta_clicks > 0 ? "+" : ""}{fmtInt(r.delta_clicks)}</td>}
              {deltaCols && <td className="px-2 py-1.5 text-right text-slate-600">{r.delta_impressions > 0 ? "+" : ""}{fmtInt(r.delta_impressions)}</td>}
              {deltaCols && <td className={`px-2 py-1.5 text-right ${r.position_change > 0 ? "text-emerald-600" : r.position_change < 0 ? "text-red-600" : "text-slate-500"}`} title="Positif = position moyenne GSC améliorée (plus proche de 1)">{r.position_change === null || r.position_change === undefined ? "—" : `${r.position_change > 0 ? "+" : ""}${String(r.position_change).replace(".", ",")}`}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleList({ rows, keyField, testid, note, onKeyClick }) {
  if (!rows || rows.length === 0) return <p className="text-xs text-slate-400 px-2 py-2">Aucune ligne.</p>;
  return (
    <div data-testid={testid} className="space-y-1">
      {note && <p className="text-[11px] text-slate-400 px-2">{note}</p>}
      {rows.map((r, idx) => (
        <div key={idx} className="flex items-center justify-between gap-2 px-2 py-1 text-xs border-b border-slate-100">
          <button data-testid={`${testid}-key-${idx}`} className="text-blue-700 hover:underline truncate text-left" onClick={() => onKeyClick?.(r[keyField])}>{r[keyField]}</button>
          <span className="text-slate-500 whitespace-nowrap">
            {r.clicks !== undefined && r.clicks !== null ? `${fmtInt(r.clicks)} clics · ${fmtInt(r.impressions)} impr.` :
              `préc. : ${fmtInt(r.previous_clicks)} clics · ${fmtInt(r.previous_impressions)} impr.`}
          </span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export default function GscInsights() {
  const { activeSite } = useSites();
  const [status, setStatus] = useState(null);
  const [insights, setInsights] = useState(null);
  const [cross, setCross] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [days, setDays] = useState(28);
  const [custom, setCustom] = useState({ start: "", end: "", active: false });
  const [tab, setTab] = useState("opportunities");
  const [pageDetail, setPageDetail] = useState(null);
  const [queryDetail, setQueryDetail] = useState(null);

  const periodParams = useCallback(() => (
    custom.active && custom.start && custom.end
      ? { start: custom.start, end: custom.end }
      : { days }
  ), [days, custom]);

  const loadAll = useCallback(async () => {
    if (!activeSite) return;
    setLoading(true);
    try {
      const params = periodParams();
      const [s, i, c] = await Promise.all([
        api.get(`/sites/${activeSite.id}/gsc/status`),
        api.get(`/sites/${activeSite.id}/gsc/insights`, { params }),
        api.get(`/sites/${activeSite.id}/gsc/audit-cross`, { params }),
      ]);
      setStatus(s.data);
      setInsights(i.data);
      setCross(c.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de chargement GSC Insights");
    } finally {
      setLoading(false);
    }
  }, [activeSite, periodParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doSync = async () => {
    if (!activeSite) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/sites/${activeSite.id}/gsc/sync`, periodParams());
      toast.success(data.status === "NO_DATA"
        ? "Synchronisation effectuée — aucune donnée disponible pour la période"
        : "Synchronisation GSC réussie");
      await loadAll();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.warning("Une synchronisation est déjà en cours pour ce site — réessayez dans quelques instants.");
      } else {
        toast.error(err?.response?.data?.detail || "Erreur de synchronisation GSC");
      }
    } finally {
      setSyncing(false);
    }
  };

  const toggleAutoSync = async () => {
    if (!activeSite) return;
    try {
      const { data } = await api.patch(`/sites/${activeSite.id}/gsc/auto-sync`, { enabled: !status?.auto_sync_enabled });
      toast.success(data.gsc_auto_sync_enabled
        ? "Synchronisation automatique quotidienne activée"
        : "Synchronisation automatique quotidienne désactivée");
      const { data: s } = await api.get(`/sites/${activeSite.id}/gsc/status`);
      setStatus(s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la mise à jour de la sync automatique");
    }
  };

  const openPage = async (url) => {
    try {
      const { data } = await api.get(`/sites/${activeSite.id}/gsc/page-detail`, { params: { url, ...periodParams() } });
      setPageDetail(data);
    } catch { toast.error("Erreur de chargement du détail de page"); }
  };
  const openQuery = async (q) => {
    try {
      const { data } = await api.get(`/sites/${activeSite.id}/gsc/query-detail`, { params: { query: q, ...periodParams() } });
      setQueryDetail(data);
    } catch { toast.error("Erreur de chargement du détail de requête"); }
  };

  if (!activeSite) {
    return (
      <div className="space-y-6">
        <PageHeader overline="Google Search Console" title="GSC Insights" description="Données de recherche réelles croisées avec vos audits SEO." />
        <p data-testid="gsc-no-site" className="text-sm text-slate-500">Sélectionnez d'abord un site.</p>
      </div>
    );
  }

  const meta = STATUS_META[status?.status] || STATUS_META.ERROR;
  const ready = insights?.status === "OK";
  const movementBlock = (mv, keyField, prefix, onClick) => (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Section title={keyField === "page" ? "Pages en progression" : "Requêtes gagnantes"} testid={`${prefix}-winners`}>
        <MovementTable rows={mv?.winners} keyField={keyField} testid={`${prefix}-winners-table`} onKeyClick={onClick} />
      </Section>
      <Section title={keyField === "page" ? "Pages en baisse" : "Requêtes en baisse"} testid={`${prefix}-losers`}>
        <p className="text-[11px] text-slate-400 mb-1">Variations observées dans les données GSC — sans test de significativité statistique, une petite variation n'est pas une alerte.</p>
        <MovementTable rows={mv?.losers} keyField={keyField} testid={`${prefix}-losers-table`} onKeyClick={onClick} />
      </Section>
      <Section title={keyField === "page" ? "Nouvelles pages observées" : "Nouvelles requêtes"} testid={`${prefix}-new`}>
        <SimpleList rows={mv?.new} keyField={keyField} testid={`${prefix}-new-list`} onKeyClick={onClick}
          note="Présentes dans les données GSC actuelles, absentes de la période précédente — aucune variation % calculable." />
      </Section>
      <Section title={keyField === "page" ? "Pages non observées" : "Requêtes non observées"} testid={`${prefix}-notobserved`}>
        <SimpleList rows={mv?.not_observed} keyField={keyField} testid={`${prefix}-notobserved-list`} onKeyClick={onClick}
          note="Non observées dans les données GSC de la période actuelle — cela ne prouve pas une désindexation (voir l'Audit)." />
      </Section>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="gsc-insights-page">
      <PageHeader overline="Google Search Console" title="GSC Insights"
        description="Performance observée dans Google Search Console, croisée avec vos audits SEO. Aucune donnée estimée." />

      {/* Statut connexion */}
      <div data-testid="gsc-status-card" className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span data-testid="gsc-status-badge" className={`px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
        {status?.property && <span data-testid="gsc-property" className="text-xs text-slate-600">Propriété : <span className="font-mono">{status.property}</span></span>}
        {status?.google_email && <span className="text-xs text-slate-500">{status.google_email}</span>}
        {status?.last_success_at && <span data-testid="gsc-last-sync" className="text-xs text-slate-500">Dernière synchro réussie : {new Date(status.last_success_at).toLocaleString("fr-CH")}{status?.last_trigger === "SCHEDULED" ? " (automatique)" : ""}</span>}
        {status?.last_error && <span data-testid="gsc-last-error" className="text-xs text-red-600 max-w-md truncate" title={status.last_error}><AlertTriangle className="w-3 h-3 inline mr-1" />Dernière tentative : échec — {status.last_error}</span>}
        <div className="ml-auto flex items-center gap-2">
          {(status?.status === "NOT_CONNECTED" || status?.status === "PROPERTY_NOT_SET") && (
            <Link to="/performance" data-testid="gsc-connect-cta"
              className="text-xs font-medium text-blue-700 hover:underline inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              {status?.status === "NOT_CONNECTED" ? "Connecter Google (page Performance)" : "Choisir la propriété GSC (page Performance)"}
            </Link>
          )}
          {status && status.status !== "NOT_CONNECTED" && status.status !== "PROPERTY_NOT_SET" && (
            <button data-testid="gsc-auto-sync-toggle" onClick={toggleAutoSync}
              title="La synchronisation quotidienne maintient les données GSC à jour sans intervention — n'affecte pas la connexion Google"
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${status.auto_sync_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
              {status.auto_sync_enabled ? "Sync auto quotidienne : activée" : "Sync auto quotidienne : désactivée"}
            </button>
          )}
          <button data-testid="gsc-sync-btn" onClick={doSync}
            disabled={syncing || !status || status.status === "NOT_CONNECTED" || status.status === "PROPERTY_NOT_SET"}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium disabled:opacity-40">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Synchroniser
          </button>
        </div>
      </div>

      {/* Sélecteur de période */}
      <div data-testid="gsc-period-selector" className="flex flex-wrap items-center gap-2">
        {[7, 28, 90].map((d) => (
          <button key={d} data-testid={`gsc-period-${d}`}
            onClick={() => { setDays(d); setCustom((c) => ({ ...c, active: false })); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${!custom.active && days === d ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {d === 90 ? "3 mois" : `${d} jours`}
          </button>
        ))}
        <div className="flex items-center gap-1.5 text-xs">
          <input type="date" data-testid="gsc-period-custom-start" value={custom.start}
            onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
            className="border border-slate-200 rounded-lg px-2 py-1.5" />
          <span className="text-slate-400">→</span>
          <input type="date" data-testid="gsc-period-custom-end" value={custom.end}
            onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
            className="border border-slate-200 rounded-lg px-2 py-1.5" />
          <button data-testid="gsc-period-custom-apply"
            onClick={() => custom.start && custom.end && setCustom((c) => ({ ...c, active: true }))}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${custom.active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            Période personnalisée
          </button>
        </div>
        {insights?.period && (
          <span data-testid="gsc-period-covered" className="text-xs text-slate-500">
            {insights.period.start} → {insights.period.end}
            {insights.previous_period && ` (vs ${insights.previous_period.start} → ${insights.previous_period.end})`}
            {insights.requested_period && insights.period.end !== insights.requested_period.end && (
              <span data-testid="gsc-finalized-window-hint" className="text-amber-600 font-medium"> — fenêtre finalisée (jours incomplets exclus)</span>
            )}
          </span>
        )}
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}

      {!loading && insights?.status === "NOT_SYNCED" && (
        <div data-testid="gsc-not-synced" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <Info className="w-4 h-4 inline mr-1.5" />
          Aucune synchronisation GSC pour cette période.
          {status?.status === "CONNECTED" ? " Lancez une synchronisation avec le bouton ci-dessus." :
            status?.status === "NOT_CONNECTED" ? " Connectez d'abord votre compte Google (page Performance)." :
              status?.status === "PROPERTY_NOT_SET" ? " Configurez d'abord la propriété Search Console du site (page Performance)." : ""}
        </div>
      )}
      {!loading && insights?.status === "INSUFFICIENT_FINALIZED_DATA" && (
        <div data-testid="gsc-insufficient-finalized" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 inline mr-1.5" />
          DONNÉES FINALISÉES INSUFFISANTES : toute la période demandée
          {insights.requested_period ? ` (${insights.requested_period.start} → ${insights.requested_period.end})` : ""} est encore en cours de traitement par Google
          {insights.first_incomplete_date ? ` (premier jour incomplet : ${insights.first_incomplete_date})` : ""}. Aucune métrique trompeuse n'est affichée — choisissez une période plus ancienne.
        </div>
      )}
      {!loading && insights?.status === "NO_DATA" && (
        <div data-testid="gsc-no-data" className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
          AUCUNE DONNÉE DISPONIBLE dans Google Search Console pour cette période (propriété {insights.property || "—"}).
        </div>
      )}

      {!loading && ready && (
        <>
          {/* Vue d'ensemble */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard title="Clics" m={insights.overview?.clicks} format={fmtInt} testid="gsc-kpi-clicks" />
            <KpiCard title="Impressions" m={insights.overview?.impressions} format={fmtInt} testid="gsc-kpi-impressions" />
            <KpiCard title="CTR" m={insights.overview?.ctr} format={fmtCtr} testid="gsc-kpi-ctr" />
            <KpiCard title="Position moyenne GSC" m={insights.overview?.position} format={fmtPos} lowerIsBetter testid="gsc-kpi-position" />
          </div>
          <p data-testid="gsc-freshness-note" className="text-[11px] text-slate-400">
            {insights.freshness_note} Synchronisé : {insights.synced_at ? new Date(insights.synced_at).toLocaleString("fr-CH") : "—"}.
            {insights.truncated && ` Jeu de données tronqué à ${fmtInt(insights.row_cap)} lignes par dimension (limite produit documentée).`}
            {" "}Source : Google Search Console (métriques brutes) — insights calculés par la plateforme (moteur v{insights.insights_version}).
          </p>

          {/* Onglets */}
          <div className="flex gap-2 border-b border-slate-200">
            {[["opportunities", "Opportunités"], ["pages", "Pages"], ["queries", "Requêtes"], ["cross", "Audit × GSC"]].map(([k, label]) => (
              <button key={k} data-testid={`gsc-tab-${k}`} onClick={() => setTab(k)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "opportunities" && (
            <div className="space-y-4">
              <Section title="Opportunités de positionnement (position moyenne GSC 4–20)" testid="gsc-pos-opportunities">
                {(insights.position_opportunities || []).length === 0
                  ? <p className="text-xs text-slate-400">Aucune opportunité 4–20 dans les données disponibles.</p>
                  : (
                    <div className="overflow-x-auto">
                      <table data-testid="gsc-pos-opportunities-table" className="w-full text-xs">
                        <thead><tr className="text-slate-500 border-b border-slate-200 text-left">
                          <th className="px-2 py-1.5">Requête</th><th className="px-2 py-1.5">Page</th>
                          <th className="px-2 py-1.5 text-right">Position moy. GSC</th>
                          <th className="px-2 py-1.5 text-right">Impressions</th>
                          <th className="px-2 py-1.5 text-right">Clics</th>
                          <th className="px-2 py-1.5 text-right">CTR</th>
                        </tr></thead>
                        <tbody>
                          {insights.position_opportunities.map((r, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50" title={r.reason}>
                              <td className="px-2 py-1.5"><button className="text-blue-700 hover:underline" onClick={() => openQuery(r.query)}>{r.query}</button></td>
                              <td className="px-2 py-1.5 max-w-[260px] truncate"><button className="text-blue-700 hover:underline truncate max-w-full" onClick={() => openPage(r.page)}>{r.page}</button></td>
                              <td className="px-2 py-1.5 text-right font-medium">{fmtPos(r.position)}</td>
                              <td className="px-2 py-1.5 text-right">{fmtInt(r.impressions)}</td>
                              <td className="px-2 py-1.5 text-right">{fmtInt(r.clicks)}</td>
                              <td className="px-2 py-1.5 text-right">{fmtCtr(r.ctr)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-[11px] text-slate-400 mt-2">Survolez une ligne pour voir pourquoi elle apparaît (critères centralisés, seuil d'impressions minimum).</p>
                    </div>
                  )}
              </Section>
              <Section title="Opportunités CTR (référence : médiane du site aux positions comparables)" testid="gsc-ctr-opportunities">
                {(insights.ctr_opportunities || []).length === 0
                  ? <p className="text-xs text-slate-400">Aucune opportunité CTR — soit les CTR sont dans la norme du site, soit la référence interne est insuffisante (aucun CTR cible universel n'est inventé).</p>
                  : (
                    <div data-testid="gsc-ctr-opportunities-table" className="space-y-2">
                      {insights.ctr_opportunities.map((r, idx) => (
                        <div key={idx} className="border border-slate-100 rounded-lg p-2.5 text-xs">
                          <div className="flex flex-wrap items-center gap-2">
                            <button className="font-medium text-blue-700 hover:underline" onClick={() => openQuery(r.query)}>{r.query}</button>
                            <button className="text-slate-500 hover:underline truncate max-w-[280px]" onClick={() => openPage(r.page)}>{r.page}</button>
                            <span className="ml-auto text-slate-600">{fmtCtr(r.ctr)} · {fmtInt(r.impressions)} impressions · position {fmtPos(r.position)}</span>
                          </div>
                          <p className="text-slate-400 mt-1">{r.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}
              </Section>
            </div>
          )}

          {tab === "pages" && movementBlock(insights.pages, "page", "gsc-pages", openPage)}
          {tab === "queries" && movementBlock(insights.queries, "query", "gsc-queries", openQuery)}

          {tab === "cross" && (
            <div className="space-y-4">
              {cross?.status === "NO_AUDIT" && (
                <div data-testid="gsc-cross-no-audit" className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                  Aucun audit disponible — lancez d'abord un <Link to="/audit" className="text-blue-700 underline">audit SEO</Link>.
                </div>
              )}
              {cross?.status === "OK" && (
                <>
                  <p className="text-[11px] text-slate-400">{cross.note}</p>
                  {(cross.high_visibility_with_issues || []).length > 0 && (
                    <Section title="Pages à forte visibilité avec problèmes SEO" testid="gsc-high-visibility">
                      <div className="space-y-1">
                        {cross.high_visibility_with_issues.map((e, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs px-2 py-1 border-b border-slate-100">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIO_CLS[e.top_priority] || "bg-slate-200"}`}>{e.top_priority || "—"}</span>
                            <button data-testid={`gsc-high-vis-url-${idx}`} className="text-blue-700 hover:underline truncate" onClick={() => openPage(e.audited_url)}>{e.audited_url}</button>
                            <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium ${SIGNAL_CLS[e.search_performance_signal]}`}>Signal {SIGNAL_LABEL[e.search_performance_signal]}</span>
                            <span className="text-slate-500 whitespace-nowrap">{e.issues_total} problème(s)</span>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                  <Section title="Toutes les URLs auditées × performance GSC observée (ordre de traitement recommandé)" testid="gsc-cross">
                    <div className="overflow-x-auto">
                      <table data-testid="gsc-cross-table" className="w-full text-xs">
                        <thead><tr className="text-slate-500 border-b border-slate-200 text-left">
                          <th className="px-2 py-1.5">URL auditée</th>
                          <th className="px-2 py-1.5">Priorité (Phase B)</th>
                          <th className="px-2 py-1.5 text-right">Problèmes</th>
                          <th className="px-2 py-1.5">Signal GSC</th>
                          <th className="px-2 py-1.5 text-right">Clics</th>
                          <th className="px-2 py-1.5 text-right">Impressions</th>
                          <th className="px-2 py-1.5 text-right">CTR</th>
                          <th className="px-2 py-1.5 text-right">Position moy. GSC</th>
                          <th className="px-2 py-1.5 text-right">Ordre recommandé</th>
                        </tr></thead>
                        <tbody>
                          {(cross.urls || []).map((e, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`gsc-cross-row-${idx}`}>
                              <td className="px-2 py-1.5 max-w-[280px] truncate"><button data-testid={`gsc-cross-url-${idx}`} className="text-blue-700 hover:underline truncate max-w-full" onClick={() => openPage(e.audited_url)}>{e.audited_url}</button></td>
                              <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIO_CLS[e.top_priority] || "bg-slate-200 text-slate-500"}`}>{e.top_priority || "—"}</span></td>
                              <td className="px-2 py-1.5 text-right">{e.issues_total}</td>
                              <td className="px-2 py-1.5"><span title={e.signal_reason} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SIGNAL_CLS[e.search_performance_signal]}`}>{SIGNAL_LABEL[e.search_performance_signal]}</span></td>
                              {e.gsc ? <MetricCells r={e.gsc} /> : <td colSpan={4} className="px-2 py-1.5 text-center text-slate-400">Aucune donnée GSC pour cette URL</td>}
                              <td className="px-2 py-1.5 text-right font-mono text-slate-600" data-testid={`gsc-cross-order-${idx}`}>{e.recommended_order}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {!cross.gsc_available && <p className="text-[11px] text-amber-600 mt-2">Signal GSC NON DISPONIBLE (aucune synchronisation pour la période) — les priorités Phase B restent valables.</p>}
                  </Section>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Drill-down page */}
      <Dialog open={!!pageDetail} onOpenChange={(o) => !o && setPageDetail(null)}>
        <DialogContent data-testid="gsc-page-drilldown" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm break-all">{pageDetail?.url}</DialogTitle>
            <DialogDescription>
              {pageDetail?.observed
                ? `Performance observée dans GSC (${pageDetail?.period?.start} → ${pageDetail?.period?.end})`
                : (pageDetail?.note || "URL non observée dans les données GSC de la période.")}
            </DialogDescription>
          </DialogHeader>
          {pageDetail?.metrics && (
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Clics", fmtInt(pageDetail.metrics.clicks), pageDetail.comparison?.clicks],
                ["Impressions", fmtInt(pageDetail.metrics.impressions), pageDetail.comparison?.impressions],
                ["CTR", fmtCtr(pageDetail.metrics.ctr), pageDetail.comparison?.ctr],
                ["Position moy. GSC", fmtPos(pageDetail.metrics.position), pageDetail.comparison?.position]].map(([l, v, m], i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500">{l}</p>
                  <p className="text-sm font-semibold">{v}</p>
                  <Delta m={m} format={l === "CTR" ? fmtCtr : l.startsWith("Position") ? fmtPos : fmtInt} lowerIsBetter={l.startsWith("Position")} />
                </div>
              ))}
            </div>
          )}
          {(pageDetail?.queries || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Requêtes disponibles dans GSC pour cette page</h4>
              <MovementTable rows={pageDetail.queries} keyField="query" testid="gsc-page-drilldown-queries" deltaCols={false}
                onKeyClick={(q) => { setPageDetail(null); openQuery(q); }} />
            </div>
          )}
          <div>
            <h4 className="text-xs font-semibold text-slate-700 mb-1">Problèmes SEO (audit)</h4>
            {(pageDetail?.issues || []).length === 0
              ? <p className="text-xs text-slate-400">Aucun problème d'audit pour cette URL.</p>
              : pageDetail.issues.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIO_CLS[it.priority] || "bg-slate-200 text-slate-500"}`}>{it.priority || "—"}</span>
                  <span className="font-medium">{it.category}</span>
                  <span className="text-slate-500 truncate">{it.message}</span>
                  {it.confidence && <span className="ml-auto text-slate-400">confiance {it.confidence}</span>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Drill-down requête */}
      <Dialog open={!!queryDetail} onOpenChange={(o) => !o && setQueryDetail(null)}>
        <DialogContent data-testid="gsc-query-drilldown" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">« {queryDetail?.query} »</DialogTitle>
            <DialogDescription>
              {queryDetail?.observed
                ? `Performance observée dans GSC (${queryDetail?.period?.start} → ${queryDetail?.period?.end})`
                : (queryDetail?.note || "Requête non observée dans les données GSC de la période actuelle.")}
            </DialogDescription>
          </DialogHeader>
          {queryDetail?.metrics && (
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Clics", fmtInt(queryDetail.metrics.clicks), queryDetail.comparison?.clicks],
                ["Impressions", fmtInt(queryDetail.metrics.impressions), queryDetail.comparison?.impressions],
                ["CTR", fmtCtr(queryDetail.metrics.ctr), queryDetail.comparison?.ctr],
                ["Position moy. GSC", fmtPos(queryDetail.metrics.position), queryDetail.comparison?.position]].map(([l, v, m], i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-500">{l}</p>
                  <p className="text-sm font-semibold">{v}</p>
                  <Delta m={m} format={l === "CTR" ? fmtCtr : l.startsWith("Position") ? fmtPos : fmtInt} lowerIsBetter={l.startsWith("Position")} />
                </div>
              ))}
            </div>
          )}
          {(queryDetail?.pages || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Pages associées</h4>
              <MovementTable rows={queryDetail.pages} keyField="page" testid="gsc-query-drilldown-pages" deltaCols={false}
                onKeyClick={(u) => { setQueryDetail(null); openPage(u); }} />
            </div>
          )}
          {(queryDetail?.issues || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Problèmes SEO des pages concernées</h4>
              {queryDetail.issues.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIO_CLS[it.priority] || "bg-slate-200 text-slate-500"}`}>{it.priority || "—"}</span>
                  <span className="font-medium">{it.category}</span>
                  <span className="text-slate-500 truncate">{it.message}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
